import React, { useMemo, useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { useTheme } from '../context/ThemeContext';
import { QRCodeCanvas } from 'qrcode.react';

const Students = () => {
  const { isDark } = useTheme();
  const [qrOpen, setQrOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    thisWeek: 0,
    totalPeople: 0,
    pickupBreakdown: [],
    statusBreakdown: [],
    schoolBreakdown: [],
  });
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState({
    status: 'pending', // Default to show pending status
    school: '',
    pickup_location: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  // State for editing number of people
  const [editingStudent, setEditingStudent] = useState(null);
  const [editPeopleCount, setEditPeopleCount] = useState(1);

  // Pickup locations based on school
  const pickupLocations = {
    'University of Abuja': [
      { value: 'boys_hostel_mini_campus', label: 'Boys Hostel (Mini Campus)' },
      { value: 'boys_hostel_main_campus', label: 'Boys Hostel (Main Campus)' },
      { value: 'girls_hostel_main_campus', label: 'Girls Hostel (Main Campus)' },
      { value: 'permanent_site_gate', label: 'Permanent Site Gate' },
      { value: 'ido_junction', label: 'Ido Junction' },
      { value: 'saoka_bridge_immigration', label: 'Saoka Bridge (Immigration)' },
      { value: 'lugbe_car_wash', label: 'Lugbe by Car Wash' },
      { value: 'lugbe_police_sign_post', label: 'Lugbe by Police Sign Post' },
    ],
    'Nasarawa State University': [
      { value: 'school_gate', label: 'School Gate' },
      { value: 'keffi_bridge', label: 'Keffi Bridge' },
      { value: 'maraba_junction', label: 'Maraba Junction' },
      { value: 'nyanya_bridge', label: 'Nyanya Bridge' },
    ],
  };

  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    school_department: '',
    pickup_location: 'boys_hostel_mini_campus',
    will_join_sunday: true,
    notes: '',
  });

  // API Base URL - should be configured in environment
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rccgtr.net/api';

  // Get system IP for QR code (test mode)
  // const getSystemIP = () => {
  //   // For test mode, use a common local network IP
  //   // In production, this should come from environment variables
  //   return '172.16.159.119:3000';
  // };
  const getSystemIP = () => {
    // Use environment variable in production, fallback to local IP for development
    if (process.env.NODE_ENV === 'production') {
      // For production, use environment variables
      return process.env.REACT_APP_PRODUCTION_URL || 
             process.env.REACT_APP_API_BASE_URL || 
             window.location.hostname;
    } else {
      // For development, use the hardcoded IP or environment variable
      return process.env.REACT_APP_DEV_URL || '172.16.159.119:3000';
    }
  };

  const formUrl = useMemo(() => `http://${getSystemIP()}/studentInterest`, []);

  // QR download support
  const qrCanvasRef = useRef(null);
  const handleDownloadQR = () => {
    try {
      const canvas = qrCanvasRef.current?.querySelector('canvas');
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = 'student-interest-qr.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {}
  };

  useEffect(() => {
    loadStats();
    loadStudents();
  }, [pagination.page, filters]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/students/stats`);
      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      } else {
        setMessage('Failed to load statistics');
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      setMessage('Error loading statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });

      const response = await fetch(`${API_BASE_URL}/students?${params}`);
      const data = await response.json();

      if (data.success) {
        setStudents(data.data);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          pages: data.pagination.pages,
        }));
      } else {
        setMessage('Failed to load students');
      }
    } catch (error) {
      console.error('Error loading students:', error);
      setMessage('Error loading students');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (studentId, newStatus) => {
    try {
      setLoading(true);
      const url =
      studentId === 'all'
        ? `${API_BASE_URL}/students/bulk-update`
        : `${API_BASE_URL}/students/${studentId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`Status updated to ${newStatus} successfully!`);
        loadStudents();
        loadStats();
      } else {
        setMessage('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setMessage('Error updating status');
    } finally {
      setLoading(false);
    }
  };

   // Function to update number of people
   const handleUpdatePeopleCount = async (studentId, newCount) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/students/${studentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ number_of_people: newCount }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('Number of people updated successfully!');
        setEditingStudent(null);
        setEditPeopleCount(1);
        loadStudents();
        loadStats(); // Reload stats to update total people count
      } else {
        setMessage('Failed to update number of people');
      }
    } catch (error) {
      console.error('Error updating number of people:', error);
      setMessage('Error updating number of people');
    } finally {
      setLoading(false);
    }
  };

  // Start editing a student's people count
  const startEditing = (student) => {
    setEditingStudent(student.id);
    setEditPeopleCount(student.number_of_people || 1);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingStudent(null);
    setEditPeopleCount(1);
  };

  // Save the edited people count
  const saveEditing = (studentId) => {
    if (editPeopleCount < 1) {
      setMessage('Number of people must be at least 1');
      return;
    }
    handleUpdatePeopleCount(studentId, editPeopleCount);
  };

  const handleFormSubmit = async e => {
    e.preventDefault();

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('Student interest form submitted successfully!');
        setFormData({
          full_name: '',
          phone: '',
          school_department: '',
          pickup_location: 'boys_hostel_mini_campus',
          will_join_sunday: true,
          notes: '',
        });
        loadStats();
        loadStudents();
      } else {
        setMessage('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setMessage('Error submitting form');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset pickup location when school changes
    if (field === 'school') {
      setFilters(prev => ({
        ...prev,
        [field]: value,
        pickup_location: value ? pickupLocations[value]?.[0]?.value || '' : '',
      }));
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = newPage => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getPickupLocationName = location => {
    // Find the label for the location value
    for (const school of Object.values(pickupLocations)) {
      const found = school.find(loc => loc.value === location);
      if (found) return found.label;
    }
    return location;
  };

  const getStatusBadge = status => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      contacted: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      confirmed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return badges[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  return (
    <Layout title="Students Platform">
      <div className="max-w-6xl mx-auto mt-6 space-y-6">
        {/* Message Display */}
        {message && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.includes('successfully')
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}
          >
            {message}
            <button onClick={() => setMessage('')} className="float-right font-bold">
              ×
            </button>
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div
            className={`${
              isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'
            } p-4 rounded-xl shadow`}
          >
            <p className="text-sm opacity-80">Total Interested</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div
            className={`${
              isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'
            } p-4 rounded-xl shadow`}
          >
            <p className="text-sm opacity-80">Total Interested This Week</p>
            <p className="text-2xl font-bold">{stats.thisWeek}</p>
          </div>
          <div
            className={`${
              isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'
            } p-4 rounded-xl shadow`}
          >
            <p className="text-sm opacity-80">Total People Invited This Week</p>
            <p className="text-2xl font-bold">{stats.totalPeople || 0}</p>
          </div>
          <div
            className={`${
              isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'
            } p-4 rounded-xl shadow`}
          >
            <p className="text-sm opacity-80">Total Pending</p>
            <p className="text-2xl font-bold">
              {stats.statusBreakdown?.find(item => item.status === 'pending')?.count || 0}
            </p>
          </div>
        </div>

        {/* Additional stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className={`${
              isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'
            } p-4 rounded-xl shadow`}
          >
            <p className="text-sm opacity-80">University of Abuja</p>
            <p className="text-2xl font-bold">
              {stats.schoolBreakdown?.find(item => item.school === 'University of Abuja')?.count ||
                0}
            </p>
          </div>
          <div
            className={`${
              isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'
            } p-4 rounded-xl shadow`}
          >
            <p className="text-sm opacity-80">Nasarawa State University</p>
            <p className="text-2xl font-bold">
              {stats.schoolBreakdown?.find(item => item.school === 'Nasarawa State University')
                ?.count || 0}
            </p>
          </div>
          <div
            className={`${
              isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'
            } p-4 rounded-xl shadow`}
          >
            <p className="text-sm opacity-80">Total Confirmed</p>
            <p className="text-2xl font-bold">
              {stats.statusBreakdown?.find(item => item.status === 'confirmed')?.count || 0}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button
            className="bg-indigo-600 text-white px-3 py-2 rounded"
            onClick={() => setQrOpen(true)}
          >
            Open QR Code
          </button>
          {/* <a className="bg-green-600 text-white px-3 py-2 rounded" href="#student-form">Add Student</a> */}
        </div>

        {/* Filters */}
        <div
          className={`${isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'} rounded-xl shadow p-4`}
        >
          <div className="flex flex-wrap gap-4 items-center">
          <div>
              <label className="block text-sm mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={e => handleFilterChange('startDate', e.target.value)}
                className={`${
                  isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                } border rounded px-3 py-2`}
              />
            </div>
            <div>
              <label className="block text-sm mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={e => handleFilterChange('endDate', e.target.value)}
                className={`${
                  isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                } border rounded px-3 py-2`}
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Status</label>
              <select
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
                className={`${
                  isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                } border rounded px-3 py-2`}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">School</label>
              <select
                value={filters.school}
                onChange={e => handleFilterChange('school', e.target.value)}
                className={`${
                  isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                } border rounded px-3 py-2`}
              >
                <option value="">All Schools</option>
                <option value="University of Abuja">University of Abuja</option>
                <option value="Nasarawa State University">Nasarawa State University</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Pickup Location</label>
              <select
                value={filters.pickup_location}
                onChange={e => handleFilterChange('pickup_location', e.target.value)}
                className={`${
                  isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                } border rounded px-3 py-2`}
                disabled={!filters.school}
              >
                <option value="">All Locations</option>
                {filters.school &&
                  pickupLocations[filters.school]?.map(location => (
                    <option key={location.value} value={location.value}>
                      {location.label}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Search</label>
              <input
                type="text"
                placeholder="Name, phone, or level..."
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                className={`${
                  isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                } border rounded px-3 py-2`}
              />
            </div>
          </div>
        </div>
        // Bulk actions
        <div className="flex gap-3">
          <button
            onClick={() => handleStatusUpdate('all', 'confirmed')}
            className="bg-green-600 text-white px-3 py-2 rounded"
          >
            Confirm All Pending
          </button>
          <button
            onClick={() => handleStatusUpdate('all', 'cancelled')}
            className="bg-red-600 text-white px-3 py-2 rounded"
          >
            Cancel All Pending
          </button>
        </div>

        {/* Table */}
        <div
          className={`${isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'} rounded-xl shadow p-4`}
        >
          <div className="flex justify-between items-center mb-3">
            <p className="font-semibold">Interested Students</p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className={`${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                      <th className="p-2 text-left">SN</th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Phone</th>
                      <th className="p-2 text-left">School</th>
                      <th className="p-2 text-left">Level</th>
                      <th className="p-2 text-left">Pickup</th>
                      <th className="p-2 text-left">Coming</th>
                      <th className="p-2 text-left">People</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student,i) => (
                      <tr
                        key={student.id}
                        className={`${isDark ? 'border-gray-700' : 'border-gray-200'} border-b`}
                      >
                        <td className="p-2">{1 + i}</td>
                        <td className="p-2">{student.full_name}</td>
                        <td className="p-2">{student.phone}</td>
                        <td className="p-2">{student.school}</td>
                        <td className="p-2">{student.school_level}</td>
                        <td className="p-2">{getPickupLocationName(student.pickup_location)}</td>
                        <td className="p-2">{student.will_join_sunday ? 'Yes' : 'No'}</td>
                        <td className="p-2">
                          {editingStudent === student.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                max="20"
                                value={editPeopleCount}
                                onChange={(e) => setEditPeopleCount(parseInt(e.target.value) || 1)}
                                className="w-16 p-1 border rounded text-center"
                              />
                              <button
                                onClick={() => saveEditing(student.id)}
                                className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                              >
                                ✓
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <div 
                              className="cursor-pointer hover:underline"
                              onClick={() => startEditing(student)}
                              title="Click to edit number of people"
                            >
                              {student.number_of_people || 1}
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          <span
                            className={`px-2 py-1 rounded text-xs ${getStatusBadge(
                              student.status
                            )}`}
                          >
                            {student.status}
                          </span>
                        </td>
                        <td className="p-2">{new Date(student.created_at).toLocaleDateString()}</td>
                        <td className="p-2">
                        {student.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleStatusUpdate(student.id, 'confirmed')}
                              className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(student.id, 'cancelled')}
                              className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* QR Code Modal */}
        {qrOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
              className={`${
                isDark ? 'bg-gray-800' : 'bg-white'
              } p-6 rounded-xl max-w-md w-full mx-4`}
            >
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-4">Student Interest QR Code</h3>
                <div ref={qrCanvasRef} className="flex justify-center mb-4">
                  <QRCodeCanvas value={formUrl} size={200} includeMargin />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDownloadQR}
                    className="bg-indigo-600 text-white px-4 py-2 rounded flex-1"
                  >
                    Download QR
                  </button>
                  <button
                    onClick={() => setQrOpen(false)}
                    className="bg-gray-600 text-white px-4 py-2 rounded flex-1"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Students;
